import { Injectable, type Provider, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TitleStrategy, type RouterStateSnapshot } from '@angular/router';
import { APP_SHELL_CONFIG } from './app-shell.config';

/**
 * 라우트 `title`(Angular 네이티브)과 셸의 `brandText`(`AppShellConfig`)를 조합해
 * `document.title`을 세팅한다. `TitleStrategy`는 root provider라 `AppShellComponent`의
 * `title` 입력을 볼 수 없으므로, 브랜드 텍스트는 반드시 설정 레벨(`brandText`)에 있어야
 * 한다 — 컴포넌트 입력 `title`은 이 전략과 무관하게 화면 표시용으로만 쓰인다.
 *
 * `provideShellTitleStrategy()`로 옵트인해야 동작한다 — 추가하지 않은 앱은 동작 변화가
 * 없고 `index.html`의 정적 `<title>`이 그대로 유지된다.
 */
@Injectable()
export class ShellTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title);
  private readonly config = inject(APP_SHELL_CONFIG, { optional: true }) ?? {};

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const parts = [this.buildTitle(snapshot), this.config.brandText].filter(
      (part): part is string => !!part,
    );
    // 라우트 title도 brandText도 없으면 index.html의 <title>을 그대로 둔다 — 인자 없는
    // provideAppShell()을 쓰는 앱이 대부분이라, 여기서 제목을 지워버리면 안 된다.
    if (parts.length === 0) return;

    const base = parts.join(this.config.documentTitleSeparator ?? ' · ');
    this.titleService.setTitle(
      this.config.documentTitleSuffix ? `${base} (${this.config.documentTitleSuffix})` : base,
    );
  }
}

export function provideShellTitleStrategy(): Provider {
  return { provide: TitleStrategy, useClass: ShellTitleStrategy };
}
